import React, { type FC, type ReactNode, type MouseEvent } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { CustomThemeButton } from '@atlaskit/button';
import { fg } from '@atlassian/jira-feature-gating';
import { FORGE_ENTITY_TYPE } from '@atlassian/jira-issue-view-common-constants/src/ecosystem-constants.tsx';
import type { ActivityItem } from '@atlassian/jira-issue-view-common-types/src/activity-item-type.tsx';
import { ForgeIssueActivityItem } from '@atlassian/jira-issue-view-forge-issue-activity/src/index.tsx';
import {
	ContextualAnalyticsData,
	fireScreenAnalyticsDeferred,
	MountEvent,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import type { SelectActivityItem } from '../../common/ui/types.tsx';
import { buttonTheme } from '../../styled.tsx';
import { ufoNameForSwitchingToActivityItem } from './utils.tsx';

const ButtonComponentNew = ({
	children,
	onClick,
	isSelected,
	testId,
	item,
}: {
	children: ReactNode;
	onClick: (event: MouseEvent, analyticsEvent: UIAnalyticsEvent) => void;
	isSelected: boolean;
	testId?: string;
	item: ActivityItem;
}) => {
	const interactionName = ufoNameForSwitchingToActivityItem(item);
	return (
		<CustomThemeButton
			appearance="subtle"
			spacing="compact"
			isSelected={isSelected}
			theme={buttonTheme(isSelected)}
			onClick={onClick}
			testId={testId}
			aria-checked={isSelected}
			role="menuitemradio"
			// eslint-disable-next-line jira/ufo/valid-labels
			interactionName={interactionName}
		>
			{children}
		</CustomThemeButton>
	);
};

const ButtonComponentOld = ({
	children,
	onClick,
	isSelected,
	testId,
}: {
	children: ReactNode;
	onClick: (event: MouseEvent, analyticsEvent: UIAnalyticsEvent) => void;
	isSelected: boolean;
	testId?: string;
}) => (
	<CustomThemeButton
		appearance="subtle"
		spacing="compact"
		isSelected={isSelected}
		theme={buttonTheme(isSelected)}
		onClick={onClick}
		testId={testId}
		aria-checked={isSelected}
		role="menuitemradio"
		interactionName="auto-generated.jira-issue-activity-feed.ui.buttons.button"
	>
		{children}
	</CustomThemeButton>
);

type Props = {
	items: ActivityItem[];
	selectedItem: ActivityItem;
	onSelectActivityItem: SelectActivityItem;
};

export const ActivityFeedButtons: FC<Props> = ({
	items,
	selectedItem,
	onSelectActivityItem,
}: Props) => (
	<ContextualAnalyticsData sourceType={SCREEN} sourceName="activityFeedButtons">
		<MountEvent onMount={(event) => fireScreenAnalyticsDeferred(event)} />
		{items.map((item: ActivityItem) => {
			const props = {
				key: item.key,
				isSelected: selectedItem.key === item.key,

				onClick: (event: MouseEvent, analyticsEvent: UIAnalyticsEvent) =>
					onSelectActivityItem(item, analyticsEvent, 'buttonItemSelected', event.timeStamp),
			};

			/**
			 * Todo: Update ForgeIssueActivityItem to use ButtonComponentNew
			 *
			 * After the cleanup of ken-1285-fix-ufo-event-for-issue-history,
			 * ForgeIssueActivityItem will keep using the ButtonComponentOld.
			 * This is done to avoid any changes to ForgeIssueActivityItem for now
			 *
			 * Updating ForgeIssueActivityItem to use ButtonComponentNew will be done in a separate task
			 */
			if (item.type === FORGE_ENTITY_TYPE) {
				return (
					<ForgeIssueActivityItem
						{...props}
						key={item.key}
						component={ButtonComponentOld}
						item={item}
					/>
				);
			}

			if (fg('ken-1285-fix-ufo-event-for-issue-history')) {
				return (
					<ButtonComponentNew
						{...props}
						key={item.key}
						testId={`issue-activity-feed.ui.buttons.${item.key}`}
						item={item}
					>
						{item.name}
					</ButtonComponentNew>
				);
			}
			return (
				<ButtonComponentOld
					{...props}
					key={item.key}
					testId={`issue-activity-feed.ui.buttons.${item.key}`}
				>
					{item.name}
				</ButtonComponentOld>
			);
		})}
	</ContextualAnalyticsData>
);
